import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AllowedRoutes, cityConfigs, defaultConfig } from '@app/core/city-configs/city-configs';
import { City } from '@app/core/models/city.model';
import { CityConfig } from '@app/core/models/city-config';
import { CityStoreService } from '@app/core/services/city-store.service';
import { UserService } from '@app/core/services/user.service';
import { Language } from '@cirklo/client';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs';

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class CityConfigService {
    private userService = inject(UserService);
    private cityStoreService = inject(CityStoreService);

    rawCityConfig!: CityConfig;
    _config = new ReplaySubject<CityConfig>(1);
    config: Observable<CityConfig> = this._config.asObservable();
    configSignal = toSignal(this._config);
    private previousCityConfigKey = 'previousCityConfig';

    constructor() {
        this.loadConfig().pipe(untilDestroyed(this)).subscribe();
    }

    /**
     * Look for user city Id. If cityId is present, make http call to fetch config.
     * If cityId is not present, use default Cirklo config
     */
    loadConfig(): Observable<CityConfig> {
        // If the user is linked to a city, fetch the city from backend, else use default
        const getConfigFromBackend = this.userService.getUserCityId().pipe(
            distinctUntilChanged(),
            filter((cityId) => !!cityId),
            switchMap((cityId: string) => this.fetchConfig(cityId)),
        );
        return combineLatest([
            getConfigFromBackend.pipe(startWith(null)),
            of(this.getPreviousCityConfigFromStorage()),
        ]).pipe(
            switchMap(([backendConfig, localConfig]) => {
                if (backendConfig) return of(backendConfig);
                if (localConfig) return of(localConfig);
                return EMPTY;
            }),
            tap((cityConfig: CityConfig) => {
                this._config.next(cityConfig);
                this.rawCityConfig = cityConfig;
            }),
        );
    }

    private saveCityConfigInStorage(cityConfig: CityConfig) {
        localStorage.setItem(this.previousCityConfigKey, JSON.stringify(cityConfig));
    }

    private getPreviousCityConfigFromStorage(): CityConfig | undefined {
        const previousConfig = localStorage.getItem(this.previousCityConfigKey);
        if (!previousConfig) {
            return defaultConfig;
        }
        let parsed;
        try {
            parsed = JSON.parse(previousConfig);
        } catch {
            return defaultConfig;
        }
        return parsed;
    }

    /**
     * This will be replaced by a call to the backend.
     * For the moment, it will take the config from the config file instead
     * @param cityId
     */
    private fetchConfig(cityId: string): Observable<CityConfig> {
        // When refactoring, skip the getCurrentCity and make http call directly with cityId
        return this.cityStoreService.getCurrentCity().pipe(
            map((city?: City) => {
                if (city?.name) {
                    const cityConfig = cityConfigs[city.name.toLowerCase()] ?? defaultConfig;
                    cityConfig.primaryColor = city.primaryColor ?? cityConfig.primaryColor;
                    cityConfig.logo = city.cityLogo?.url ?? cityConfig.logo;
                    cityConfig.migratedToOtzy = city.migratedToOtzy != null;
                    return cityConfig;
                }
                return defaultConfig;
            }),
            tap((cityConfig: CityConfig) => this.saveCityConfigInStorage(cityConfig)),
        );
    }

    getAllowedRoutes(): Observable<string[]> {
        return combineLatest([this.config, this.userService.userAllowedRoleRoutes]).pipe(
            map(([config, allowedRoutesWithRoles]) => {
                const allowedRoutes = [...config.allowedRoutes];
                if (
                    !allowedRoutesWithRoles.includes(AllowedRoutes.GENERATE_VOUCHER) &&
                    allowedRoutes.includes(AllowedRoutes.GENERATE_VOUCHER)
                ) {
                    // User is not allowed to generate voucher
                    allowedRoutes.splice(allowedRoutes.indexOf(AllowedRoutes.GENERATE_VOUCHER), 1);
                }
                if (
                    !allowedRoutesWithRoles.includes(AllowedRoutes.USERS) &&
                    allowedRoutes.includes(AllowedRoutes.USERS)
                ) {
                    // User is not allowed to view dashboard users page
                    allowedRoutes.splice(allowedRoutes.indexOf(AllowedRoutes.USERS), 1);
                }
                return allowedRoutes;
            }),
        );
    }

    get logoPath(): Observable<string> {
        return this.config.pipe(map((config: CityConfig) => config.logo));
    }

    get headerName(): Observable<string> {
        return this.config.pipe(map((config: CityConfig) => config.name));
    }

    get senderOnVoucher(): Observable<boolean | undefined> {
        return this.config.pipe(map((config) => config.senderOnVoucher));
    }

    get messageOnVoucher(): Observable<boolean | undefined> {
        return this.config.pipe(map((config: CityConfig) => config.messageOnVoucher));
    }

    get messageOnVoucherLength(): Observable<number | undefined> {
        return this.config.pipe(map((config: CityConfig) => config.messageOnVoucherLength));
    }

    get messageOnVoucherSenderLength(): Observable<number | undefined> {
        return this.config.pipe(map((config: CityConfig) => config.messageOnVoucherSenderLength));
    }

    get voucherLanguages(): Observable<Language[] | undefined> {
        return this.config.pipe(map((config: CityConfig) => config.voucherLanguages));
    }

    getExpenseReportHeaderImage(): Observable<string> {
        return this.config.pipe(
            map((config: CityConfig) => config.expenseReportHeaderImage || defaultConfig.expenseReportHeaderImage!),
        );
    }

    get primaryColor(): Observable<string> {
        return this.config.pipe(map((config: CityConfig) => config.primaryColor));
    }

    get campaigns(): Observable<any[] | undefined> {
        return this.config.pipe(map((config: CityConfig) => config.campaigns));
    }
}
